import type { CostEstimate, CostEstimateItem } from '@elka/types';
import { normSection } from '@elka/utils';
import type { SelectedPricesMap } from '../types';

/**
 * Effective margin % for a given chapter key. Per-chapter override beats the global value.
 * This is the string-key primitive; prefer `resolveItemMarkupPercent` for per-item resolution.
 */
export function resolveMarkupPercent(
  estimate: Pick<CostEstimate, 'marginPercent' | 'marginPercentByChapter'>,
  chapterKey?: string
): number {
  // chapterKey can be '' (the "ללא פרק" bucket for uncoded items) — a valid override key,
  // so guard on `!== undefined` rather than truthiness.
  if (
    chapterKey !== undefined &&
    estimate.marginPercentByChapter &&
    chapterKey in estimate.marginPercentByChapter
  ) {
    return estimate.marginPercentByChapter[chapterKey] ?? estimate.marginPercent;
  }
  return estimate.marginPercent;
}

/**
 * Effective margin % for a single item, resolved **most-specific-prefix-first**.
 *
 * Overrides in `marginPercentByChapter` may be keyed at any section depth — chapter
 * ("8.1"), sub-chapter ("8.1.2"), or deeper — so a BoQ whose sections share a leading
 * wrapper segment (e.g. 01.8.1.*, 01.8.2.*) can carry per-sub-section margins. The item's
 * section is walked from coarsest to finest prefix and the deepest matching key wins
 * (a sub-chapter override beats its parent chapter, which beats the global default).
 * A `pricingChapterOverrides` entry relocates the item to another section first.
 *
 * `item` is a minimal structural type (not `CostEstimateItem`) so callers can also pass a
 * `QuoteItem`, whose `itemId`/`section` are optional.
 */
export function resolveItemMarkupPercent(
  estimate: Pick<
    CostEstimate,
    'marginPercent' | 'marginPercentByChapter' | 'pricingChapterOverrides'
  >,
  item: { itemId?: string; section?: string }
): number {
  const map = estimate.marginPercentByChapter;
  if (!map) return estimate.marginPercent;
  const override =
    item.itemId != null ? estimate.pricingChapterOverrides?.[item.itemId] : undefined;
  const norm = normSection((override != null ? override : item.section) || '');
  if (norm === '') {
    return '' in map ? (map[''] ?? estimate.marginPercent) : estimate.marginPercent;
  }
  const parts = norm.split('.');
  let pct = estimate.marginPercent;
  let prefix = '';
  for (let d = 0; d < parts.length; d++) {
    prefix = d === 0 ? parts[0] : `${prefix}.${parts[d]}`;
    if (prefix in map) pct = map[prefix] ?? estimate.marginPercent;
  }
  return pct;
}

/**
 * % a section inherits when it has no own override: the deepest ANCESTOR override
 * (e.g. a "1.8" override seen by a "1.8.1" key), else the global default. Mirrors
 * resolveItemMarkupPercent's most-specific-prefix walk but stops short of the key's
 * own segment, so callers can distinguish an inherited % from an own override.
 */
export function resolveInheritedMarkupPercent(
  byChapter: Record<string, number>,
  key: string,
  globalPct: number
): number {
  if (!key) return globalPct;
  const parts = key.split('.');
  let pct = globalPct;
  let prefix = '';
  for (let d = 0; d < parts.length - 1; d++) {
    prefix = d === 0 ? parts[0] : `${prefix}.${parts[d]}`;
    const v = byChapter[prefix];
    if (v !== undefined) pct = v;
  }
  return pct;
}

/**
 * Cost-plus-markup: clientPrice = cost × (1 + margin/100).
 * Negative margins are clamped to 0.
 */
export function applyMarkupToPrice(costPrice: number, marginPercent: number): number {
  if (!isFinite(costPrice) || costPrice <= 0) return 0;
  const safeMargin = Math.max(0, marginPercent);
  return costPrice * (1 + safeMargin / 100);
}

interface MarkupContext {
  estimate: Pick<
    CostEstimate,
    'marginPercent' | 'marginPercentByChapter' | 'pricingChapterOverrides'
  >;
  selectedPrices: SelectedPricesMap;
}

/**
 * Return a new items array with clientPrice computed from the current selectedPrice
 * and the effective margin for the item's chapter.
 *
 * - Items with no selection get clientPrice=undefined (keep whatever was there, don't
 *   invent a value).
 * - Header rows are skipped entirely.
 */
export function applyMarkupToItems(
  items: CostEstimateItem[],
  ctx: MarkupContext
): CostEstimateItem[] {
  return items.map((item) => {
    if (item.isHeader) return item;
    const selected = ctx.selectedPrices[item.itemId];
    if (!selected) return item;
    const margin = resolveItemMarkupPercent(ctx.estimate, item);
    const clientPrice = applyMarkupToPrice(selected.price, margin);
    return { ...item, clientPrice };
  });
}

/**
 * Section-aware target price: Σ over sections of `sectionCost × (1 + sectionMargin/100)`,
 * plus labor + equipment at the global margin (they belong to no section).
 *
 * `getUnitCost(item)` resolves each item's material unit cost — the caller chooses the source:
 * the matrix passes a selectedPrices-only resolver (a cleared price must read 0), while the
 * תמחור page passes one that falls back to `priceEntries` (matching how it displays costs).
 * Each item's margin is its per-chapter override or the global default. With an empty
 * `marginPercentByChapter` this equals `calculateFinalPrice(totalCost, margin)`.
 */
export function calculateChapterAwareTargetPrice(
  estimate: Pick<
    CostEstimate,
    | 'items'
    | 'marginPercent'
    | 'marginPercentByChapter'
    | 'pricingChapterOverrides'
    | 'laborCost'
    | 'equipmentTotalCost'
  >,
  getUnitCost: (item: CostEstimateItem) => number
): number {
  let materialsTarget = 0;
  for (const item of estimate.items) {
    if (item.isHeader) continue;
    const cost = getUnitCost(item) * item.quantity;
    if (cost <= 0) continue;
    const margin = Math.max(0, resolveItemMarkupPercent(estimate, item));
    materialsTarget += cost * (1 + margin / 100);
  }
  const nonSectionCost = (estimate.laborCost || 0) + (estimate.equipmentTotalCost || 0);
  const nonSectionTarget = nonSectionCost * (1 + Math.max(0, estimate.marginPercent) / 100);
  return materialsTarget + nonSectionTarget;
}

/**
 * Effective list-discount % for a משכ"ל item, resolved most-specific-prefix-first
 * (same walk as {@link resolveItemMarkupPercent}, reused with `marginPercent: 0` as
 * the "no override" base so a missing key resolves to 0, not some ambient margin).
 * Positive = discount, negative = addition; the value is NOT clamped — deduction
 * lines (negative clientPrice) and >100% discounts must pass through unchanged.
 */
export function resolveListDiscountPercent(
  estimate: Pick<CostEstimate, 'listDiscountByChapter' | 'pricingChapterOverrides'>,
  item: { itemId?: string; section?: string }
): number {
  return resolveItemMarkupPercent(
    {
      marginPercent: 0,
      marginPercentByChapter: estimate.listDiscountByChapter ?? {},
      pricingChapterOverrides: estimate.pricingChapterOverrides,
    },
    item
  );
}

/**
 * Apply a signed list discount: finalPrice = round2(price × (1 − pct/100)).
 * Unlike {@link applyMarkupToPrice} this does NOT clamp — a 0 or negative
 * `price` (deduction line) and any sign/magnitude of `pct` pass straight through.
 */
export function applyListDiscount(price: number, pct: number): number {
  return Math.round(price * (1 - pct / 100) * 100) / 100;
}

/**
 * משכ"ל unit price: the item's list `clientPrice` after its effective chapter discount.
 */
export function resolveMashkalUnitPrice(
  estimate: Pick<CostEstimate, 'listDiscountByChapter' | 'pricingChapterOverrides'>,
  item: { itemId?: string; section?: string; clientPrice?: number }
): number {
  return applyListDiscount(item.clientPrice ?? 0, resolveListDiscountPercent(estimate, item));
}

/**
 * Σ over non-header items of resolveMashkalUnitPrice(item) × item.quantity — the
 * adjusted משכ"ל BoQ total (after chapter discounts), shown in Stage 3 as the basis for
 * the "suggested global discount" (target ÷ this total). This is a REVENUE-side display
 * figure only: it does not feed `targetPrice` or any cost calculation — mashkal's cost
 * side and targetPrice are computed exactly like boq-with-prices (flat calculateFinalPrice
 * on totalCost), since the chapter discount only affects the client-facing quote lines.
 */
export function sumMashkalListTotal(
  estimate: Pick<CostEstimate, 'items' | 'listDiscountByChapter' | 'pricingChapterOverrides'>
): number {
  let total = 0;
  for (const item of estimate.items) {
    if (item.isHeader) continue;
    total += resolveMashkalUnitPrice(estimate, item) * item.quantity;
  }
  return total;
}

/**
 * Update the per-chapter margin map for a single chapter. Passing `undefined` removes
 * the override (reverts that chapter to the global margin).
 */
export function setChapterMarkup(
  map: CostEstimate['marginPercentByChapter'],
  chapterKey: string,
  percent: number | undefined
): NonNullable<CostEstimate['marginPercentByChapter']> {
  const next = { ...(map || {}) };
  if (percent === undefined || Number.isNaN(percent)) {
    delete next[chapterKey];
  } else {
    next[chapterKey] = percent;
  }
  return next;
}
